import {
  Box2,
  Camera,
  Frustum,
  Group,
  Matrix4,
  OrthographicCamera,
  PerspectiveCamera,
  PlaneHelper,
  Sphere,
  Vector2,
  Vector3,
} from "three";
import axios, { AxiosResponse, CancelTokenSource } from "axios";
import { config } from "../../config/config";
import { MeasurementType } from "../measurement/types";
import { IVolumeCalculationMeasurement } from "../measurement/VolumeCalculationMeasurement";
import { FrustumUtil } from "../utils/math-utils";
import { Viewer } from "../viewer";
import { PointCloudFactory } from "./PointCloud";
import { IPointCloudTile } from "./PointCloudTile";
import { Queue } from "queue-typescript";
import { PointsMaterialHandler } from "./PointCloudMaterialHandler";
import { ORIGINAL, ITextScale, SCENE_TYPE } from "../config/ViewerConfig";

interface requestObject {
  objectName: string;
  floor: string;
  type: string;
  projectId: string;
  config: any;
}

//Root tile will always be loaded
export class PointCloudStreamHandler {
  private _viewer: Viewer;
  private _rgbGroup: Group;
  // private _pickGroup: Group;
  private _rootTile: IPointCloudTile;
  private _camerafrustum: Frustum;
  private _rootURL: string | { baseKey: string; bucket: string };
  private _debugGroup: Group;
  private _pointCloudFactory: PointCloudFactory;
  private _type: string;
  private _projectId: string;
  private _timestamp: string;
  // private _isHeatMapVisible: boolean;
  private _tSplitThresHoldNow: number;
  private _isDebug: boolean;
  private _workers: any[];
  private _tileMapPickIndexToUrl: Map<number, string>; // tile url to all available tileMap
  private _usableIndex: Queue<number>;
  private _pointCloudMaterialHandler: PointsMaterialHandler;

  // ===== Point-budget tile selection =====
  // Max total points rendered per view (replaces the old fixed tile-count cap).
  private _pointBudget = 3_000_000;
  // Nodes projecting smaller than this pixel threshold prune their whole subtree (screen-space error).
  private _minNodePx = 70;
  // Renderer-side point dedup ledger: the cumulative octree stores the same
  // point at multiple levels (bit-exact Float32 coords); a point already
  // rendered by another tile is skipped.
  private _occupiedKeys: Set<number> = new Set();
  private _tileDedup: Map<string, { keys: number[] }> = new Map();

  public getRgbGroup() {
    return this._rgbGroup;
  }

  public getDebugRenderable() {
    return this._debugGroup;
  }

  public getTileMapPickIndexToUrl() {
    return this._tileMapPickIndexToUrl;
  }

  public setTileMapPickIndexToUrlForOriginal() {
    this._tileMapPickIndexToUrl.set(0, ORIGINAL);
  }

  numberToUint8Array = (num: number): Uint8Array => {
    if (num < 0 || num > 16777216 || !Number.isInteger(num)) {
      throw new Error(
        "Input must be a non-negative integer within the range of 0 to 16777216."
      );
    }

    const byteArray = new Uint8Array(3);
    byteArray[0] = num & 0xff;
    byteArray[1] = (num >> 8) & 0xff;
    byteArray[2] = (num >> 16) & 0xff;

    return byteArray;
  };

  public makePickIndex = (num: number): Uint8Array => {
    const res = new Uint8Array(num * 3);

    for (let i = 0; i < num; i++) {
      const startIndex = i * 3;
      // encode i + 1 so RGB(0,0,0) can only ever be background —
      // the decoder in PointCloudBVH subtracts 1 again
      const uint8Array = this.numberToUint8Array(i + 1);
      res.set(uint8Array, startIndex);
    }

    return res;
  };

  constructor(
    viewer: Viewer,
    pointCloudMaterialHandler: PointsMaterialHandler,
    rootTile: IPointCloudTile,
    rootUrl: string | { baseKey: string; bucket: string },
    pointCloudFactory: PointCloudFactory,
    type: string,
    projectId: string,
    timestamp: string
  ) {
    this._viewer = viewer;
    this._pointCloudMaterialHandler = pointCloudMaterialHandler;
    this._rgbGroup = new Group();
    // this._pickGroup = new Group();
    this._camerafrustum = new Frustum();
    this._rootTile = rootTile;
    this._rootURL = rootUrl;
    this._pointCloudFactory = pointCloudFactory;
    this._type = type;
    this._projectId = projectId;
    this._timestamp = timestamp;
    // this._isHeatMapVisible = true;

    this._debugGroup = new Group();
    this._tSplitThresHoldNow = 1;
    this._workers = [];

    this._tileMapPickIndexToUrl = new Map();

    this._usableIndex = new Queue();
  }

  public getMarkedToLoadTiles(): Map<string, IPointCloudTile[]> {
    const result: Map<string, IPointCloudTile[]> = new Map();
    const _parseTiles = (tile: IPointCloudTile) => {
      const t = result.get(tile.loadingStatus);
      if (t) {
        t.push(tile);
      } else {
        result.set(tile.loadingStatus, [tile]);
      }

      for (const child of tile.children) {
        _parseTiles(child);
      }
    };
    for (const c of this._rootTile.children) {
      _parseTiles(c);
    }
    return result;
  }

  public getRenderedTiles(): IPointCloudTile[] {
    const result: IPointCloudTile[] = [];
    const _parseTiles = (tile: IPointCloudTile) => {
      if (tile.pointcloud) {
        result.push(tile);
      }
      for (const child of tile.children) {
        _parseTiles(child);
      }
    };
    for (const c of this._rootTile.children) {
      _parseTiles(c);
    }
    return result;
  }

  /**
   * Renderer-side point dedup. The cumulative octree stores the same point at
   * multiple levels (bit-exact Float32 — parent voxels are cell averages, and a
   * cell holding a single point averages to exactly that point; measured 1.71x
   * on real project data). The 96 coordinate bits are mixed down to a 53-bit
   * key; points already rendered elsewhere are dropped. Returns filtered
   * vertices/colors and records keys per tile so deleting the tile releases them.
   */
  private dedupFilterTile(
    url: string,
    vertices: Float32Array,
    colors: Float32Array
  ): { vertices: Float32Array; colors: Float32Array } {
    const n = Math.floor(vertices.length / 3);
    const prev = this._tileDedup.get(url);
    if (prev) for (const k of prev.keys) this._occupiedKeys.delete(k);
    const dv = new DataView(vertices.buffer, vertices.byteOffset, n * 12);
    const keys: number[] = [];
    const keepIdx: number[] = [];
    for (let i = 0; i < n; i++) {
      const o = i * 12;
      const a = dv.getUint32(o, true);
      const b = dv.getUint32(o + 4, true);
      const c = dv.getUint32(o + 8, true);
      let h1 = (a ^ 0x9e3779b9) >>> 0;
      h1 = (Math.imul(h1, 0x85ebca6b) ^ b) >>> 0;
      h1 = (Math.imul(h1, 0xc2b2ae35) ^ c) >>> 0;
      let h2 = (c ^ 0x27d4eb2f) >>> 0;
      h2 = (Math.imul(h2, 0x165667b1) ^ a) >>> 0;
      h2 = (Math.imul(h2, 0x9e3779b9) ^ b) >>> 0;
      const key = h1 * 0x200000 + (h2 >>> 11); // 32 + 21 = 53 bit
      if (this._occupiedKeys.has(key)) continue; // already rendered by another tile → duplicate, drop
      this._occupiedKeys.add(key);
      keys.push(key);
      keepIdx.push(i);
    }
    this._tileDedup.set(url, { keys });
    if (keepIdx.length === n) return { vertices, colors }; // no duplicates
    const fv = new Float32Array(keepIdx.length * 3);
    const fc = new Float32Array(keepIdx.length * 3);
    for (let j = 0; j < keepIdx.length; j++) {
      const s = keepIdx[j] * 3;
      const d = j * 3;
      fv[d] = vertices[s];
      fv[d + 1] = vertices[s + 1];
      fv[d + 2] = vertices[s + 2];
      fc[d] = colors[s];
      fc[d + 1] = colors[s + 1];
      fc[d + 2] = colors[s + 2];
    }
    return { vertices: fv, colors: fc };
  }

  /**
   * Potree-style tile selection by screen-space error + point budget.
   * Each node: project its bounding sphere to a pixel radius; prune the whole
   * subtree when outside the frustum or below `minNodePx`; weight = pixel
   * radius x screen-centre bonus; sort descending and accept node sizes until
   * `pointBudget` is filled. Coarse + fine accumulate (additive), matching the
   * current render scheme.
   * @returns wanted = tiles to display; ordered = coarse/central-first fetch order.
   */
  private selectWantedTiles(camera: Camera): {
    wanted: Set<IPointCloudTile>;
    ordered: IPointCloudTile[];
  } {
    const wanted = new Set<IPointCloudTile>();
    const ordered: IPointCloudTile[] = [];
    if (!this._rootTile) return { wanted, ordered };

    const canvas = this._viewer.getCanvas?.();
    const screenH = (canvas && canvas.clientHeight) || 1080;

    const persp = camera as PerspectiveCamera;
    const ortho = camera as OrthographicCamera;
    const isOrtho = !!(camera as any).isOrthographicCamera;
    const slope = isOrtho
      ? 0
      : Math.tan((((persp.fov ?? 60) * Math.PI) / 180) / 2);
    const orthoPxPerUnit = isOrtho
      ? (screenH * (ortho.zoom ?? 1)) /
        Math.max(ortho.top - ortho.bottom, 1e-6)
      : 0;
    const camPos = camera.position;

    // pm = projection * viewInverse → node centres to NDC for the centre bonus.
    const pm = new Matrix4().multiplyMatrices(
      camera.projectionMatrix,
      camera.matrixWorldInverse
    );

    const _sphere = new Sphere();
    const _ndc = new Vector3();

    const projR = (center: Vector3, radius: number): number => {
      if (isOrtho) return radius * orthoPxPerUnit;
      const dist = Math.max(camPos.distanceTo(center), 1e-3);
      if (dist - radius < 0) return Number.MAX_VALUE; // camera is inside the node
      return (radius * 0.5 * screenH) / (slope * dist);
    };
    const centreBonus = (center: Vector3): number => {
      _ndc.copy(center).applyMatrix4(pm);
      const d = Math.hypot(_ndc.x, _ndc.y); // 0 at screen centre
      return 1 / (1 + 2.5 * d); // centre ≈1, corner ≈0.2
    };

    const cand: { tile: IPointCloudTile; w: number }[] = [];
    const stack: IPointCloudTile[] = [...this._rootTile.children];

    while (stack.length) {
      const tile = stack.pop() as IPointCloudTile;
      let pruneSubtree = false;

      // Empty nodes (size 0) have no bin file: not scored, but children are still visited.
      if (tile.size > 0) {
        // Cull with tileBBox — it bounds all descendants (boundingBox only bounds this node's own points).
        const intersection = FrustumUtil.intersectsBox(
          this._camerafrustum,
          tile.tileBBox ?? tile.boundingBox,
          this._tSplitThresHoldNow
        );
        if (
          intersection === FrustumUtil.TileIntersectsFrustum.noIntersection
        ) {
          pruneSubtree = true; // outside the frustum → drop the whole subtree
        } else {
          tile.boundingBox.getBoundingSphere(_sphere);
          const center = _sphere.center.clone();
          const spr = projR(center, _sphere.radius);
          if (spr < this._minNodePx) {
            pruneSubtree = true; // too small on screen → drop the whole subtree
          } else {
            cand.push({ tile, w: spr * centreBonus(center) });
          }
        }
      }

      if (!pruneSubtree) {
        for (const c of tile.children) stack.push(c);
      }
    }

    cand.sort((a, b) => b.w - a.w); // big + central nodes first

    let used = 0;
    const budget = this._pointBudget;
    for (const { tile } of cand) {
      if (used + tile.size > budget) continue; // skip; keep filling with smaller nodes
      wanted.add(tile);
      ordered.push(tile);
      used += tile.size;
    }
    return { wanted, ordered };
  }

  public async fetchAndRenderTiles(
    tilesToFetch: IPointCloudTile[],
    tileFloor: string[],
    tileTypes: string[],
    tileProjectIds: string[],
    abortController: AbortController,
    timestamp: string
  ): Promise<IPointCloudTile[]> {
    const volMeasurements = this._viewer.getMeasurementByType(
      MeasurementType.VOLUME_CALCULATION
    );

    const CancelTokenSource = axios.CancelToken.source();

    const binsRequests: requestObject[][] = [];
    for (let i = 0; i < tilesToFetch.length; i++) {
      binsRequests.push([
        {
          objectName: "points.bin",
          floor: tileFloor[i],
          type: tileTypes[i],
          projectId: tileProjectIds[i],
          config: {
            // withCredentials: true,
            responseType: "arraybuffer",
            signal: abortController.signal,
          },
        },
        {
          objectName: "colors.bin",
          floor: tileFloor[i],
          type: tileTypes[i],
          projectId: tileProjectIds[i],
          config: {
            // withCredentials: true,
            responseType: "arraybuffer",
            signal: abortController.signal,
          },
        },
      ]);
    }

    try {
      const makeFetchUrl = (binRequest: any): [string, boolean] => {
        let rootFlag = false;

        let folderName: string;
        if (binRequest.type === "simple") {
          folderName = "point_cloud_split_upload";
        } else if (binRequest.type === "sfm") {
          folderName = "point_cloud_split_SfM";
        } else if (binRequest.type === "helmert") {
          folderName = "point_cloud_split_Helmert";
        } else if (binRequest.type === "ar") {
          folderName = "AR/point_cloud_split";
        }

        let name: string;
        if (binRequest.floor === "first") {
          name = binRequest.objectName;
          rootFlag = true;
        } else {
          const floor = binRequest.floor.split("-").join("/"); // "-"を"/"で置換
          name = floor + "/" + binRequest.objectName;
        }

        let hostName = config.serverHost.includes("localhost")
          ? "http://localhost:9090/quick3d"
          : config.serverHost;
        return [
          `${hostName}/project_${binRequest.projectId}/${folderName}/${name}?timestamp=${timestamp}`,
          rootFlag,
        ];
      };

      const renderTile = (index: number, binsData: any) => {
        const filtered = this.dedupFilterTile(
          tilesToFetch[index].url,
          new Float32Array(binsData.points),
          new Float32Array(binsData.colors)
        );
        let vertices = filtered.vertices;
        let colors = filtered.colors;
        let indices = this.makePickIndex(vertices.length / 3);

        let tileIndex: number;

        if (this._usableIndex.length !== 0) {
          tileIndex = this._usableIndex.dequeue();
        } else {
          tileIndex = this._tileMapPickIndexToUrl.size;
        }

        this._tileMapPickIndexToUrl.set(tileIndex, tilesToFetch[index].url);

        const pointCloud = this._pointCloudFactory.createPointCloud(
          {
            vertices: vertices,
            color: colors,
            indices: indices,
          },
          this._pointCloudMaterialHandler.pointMaterial,
          tilesToFetch[index],
          { orthographic: 0.09, perspective: 0.18 } as ITextScale,
          this._pointCloudMaterialHandler.createPickMaterial(tileIndex)
        );

        for (const volMeasurement of volMeasurements as IVolumeCalculationMeasurement[]) {
          volMeasurement.heatMapVisibility &&
            pointCloud.applyHeightBasedColor(
              volMeasurement.pointColorRangeInfo,
              volMeasurement.polygonMesh,
              new Box2().setFromPoints(
                volMeasurement.polygonMesh.vertices as unknown as Vector2[]
              )
            );
        }

        const mesh = pointCloud.getMesh();
        this._rgbGroup.add(mesh);

        tilesToFetch[index].pointcloud = pointCloud;
        tilesToFetch[index].loadingStatus = "loaded";
        if (tilesToFetch.length !== binsRequests.length) {
          console.error("tilesToFetch and response doesn't match");
        }

        if (this._isDebug) {
          const debug = pointCloud.getDebug();
          this._debugGroup.add(debug);
        }

        this._viewer.updateRenderer();
      };

      const MAX_CONCURRENCY = 3;

      // workerがある場合は終了
      while (true) {
        const worker = this._workers.pop();
        if (worker === undefined) {
          break;
        } else {
          worker.terminate();
        }
      }

      let i = 0;
      let fetchAndRenderTilePromises: Promise<any>[] = [];

      // リクエストが残っている or promiseが残っている
      while (i < binsRequests.length || 0 < fetchAndRenderTilePromises.length) {
        // WebWorkerを使ってデータ取得 => render
        const fetchAndRenderTile = async (
          binsUrl: { points: string; colors: string },
          index: number,
          rootFlag: boolean
        ) => {
          return new Promise<any>((resolve, reject) => {
            const worker = new Worker(
              `${process.env.REACT_APP_WEB_SERVER_URL}/PointCloudLoadWorker/PointCloudLoadWorker.js`
            );

            // ベースとなるrootタイルはworker終了の管理をする必要がない
            if (!rootFlag) {
              this._workers.push(worker);
            }

            worker.postMessage(binsUrl);

            worker.onmessage = function (event: any) {
              if (event.data === undefined) {
                reject();
              } else {
                renderTile(index, event.data);
                resolve(event.data);
              }

              worker.terminate();
            };
          });
        };

        // 次に並列処理するpromiseの準備: リクエストが残っている or fetchAndRenderTilePromiseを最大並列数を超えない
        while (
          i < binsRequests.length &&
          fetchAndRenderTilePromises.length < MAX_CONCURRENCY
        ) {
          const binsRequest = binsRequests[i];

          let binsUrl: { points: string; colors: string } = {
            points: "",
            colors: "",
          };
          let pointsUrl: [string, boolean] = makeFetchUrl(binsRequest[0]);
          binsUrl["points"] = pointsUrl[0];

          let colorsUrl: [string, boolean] = makeFetchUrl(binsRequest[1]);
          binsUrl["colors"] = colorsUrl[0];

          let rootFlag = false;
          if (pointsUrl[1] === true && colorsUrl[1] === true) {
            rootFlag = true;
          }

          fetchAndRenderTilePromises.push(
            fetchAndRenderTile(binsUrl, i, rootFlag)
          );
          ++i;
        }

        // 配列の中の全てのpromiseを走らせ，最初に完了したindexを取得
        const completedIndex = await Promise.race(
          fetchAndRenderTilePromises.map((p, index) => p.then(() => index))
        );

        // 完了したpromiseを配列から削除
        fetchAndRenderTilePromises.splice(completedIndex, 1);
      }

      return tilesToFetch;
    } catch (e) {
      if (axios.isCancel(e)) {
        console.log("auto cancelation by axios");
      } else {
        // 読み込めない場合タイル分割閾値を半分にし再読み込み
        if (e instanceof Error) {
          CancelTokenSource.cancel();
          this._viewer.updateRenderer();
        } else {
          throw e;
        }
      }
    }
  }

  public async execFetchAndRenderTiles(
    tiles: IPointCloudTile[],
    abortController: AbortController
  ) {
    return new Promise<IPointCloudTile[]>((resolve, reject) => {
      const tilesToFetch = [];
      const tileUrls = [];
      const tileTypes = [];
      const tileProjectIds = [];

      for (const tile of tiles) {
        if (tile.loadingStatus !== "loaded") {
          tilesToFetch.push(tile);
          const url = tile.url.split("/").join("-"); // "/"を"-"で置換
          tileUrls.push(url);
          tileTypes.push(this._type);
          tileProjectIds.push(this._projectId);
          tile.loadingStatus = "loading";
        }
      }

      if (tilesToFetch) {
        this.fetchAndRenderTiles(
          tilesToFetch,
          tileUrls,
          tileTypes,
          tileProjectIds,
          abortController,
          this._timestamp
        )
          .then((tilesFetched) => {
            resolve(tilesFetched);
          })
          .catch((e) => {
            reject(e);
          });
      } else {
        resolve([]);
      }
    });
  }

  public async deleteTiles(tilesToDelete: IPointCloudTile[]) {
    return new Promise<IPointCloudTile[]>((resolve, reject) => {
      for (const tileToDelete of tilesToDelete) {
        if (tileToDelete.pointcloud) {
          // console.log("dispose");
          tileToDelete.pointcloud.dispose();
          tileToDelete.pointcloud = undefined;
          tileToDelete.loadingStatus = "removed";

          // release this tile's dedup keys so other tiles may render those points again
          const dd = this._tileDedup.get(tileToDelete.url);
          if (dd) {
            for (const k of dd.keys) this._occupiedKeys.delete(k);
            this._tileDedup.delete(tileToDelete.url);
          }

          this._tileMapPickIndexToUrl.forEach((value, key) => {
            if (value === tileToDelete.url) {
              this._tileMapPickIndexToUrl.delete(key);
              this._usableIndex.enqueue(key);
            }
          });
        }
      }
      resolve([]);
    });
  }

  /**
   *
   * 初期化・カメラ移動完了時に以下を実行
   *     カメラ範囲内のタイルを取得
   *     ロードされていないタイルのみをLoadする
   * @param camera
   * @param abortController
   * @returns
   */
  public async onCameraChange(
    camera: Camera,
    abortController: AbortController
  ) {
    this._camerafrustum.setFromProjectionMatrix(
      new Matrix4().multiplyMatrices(
        camera.projectionMatrix,
        camera.matrixWorldInverse
      )
    );

    // 透視投影の点の大きさ調整用_マテリアルにrootタイルのBBOX対角線長とカメラの座標をセット
    this._pointCloudMaterialHandler?.setCameraInfo(
      this._viewer.getRootLength(),
      this._viewer.getCamera().position
    );

    const fetchAndRenderLayer = async (tiles: IPointCloudTile[]) => {
      if (tiles.length > 0) {
        try {
          await this.execFetchAndRenderTiles(tiles, abortController);
        } catch (error) {
          // console.error("Error in fetching and rendering tiles", error);
        }
      } else {
        // console.log("No tiles to fetch and render");
      }
    };

    if (this._rootTile && this._rootTile.isVisible) {
      // SSE + point-budget node selection
      const { wanted, ordered } = this.selectWantedTiles(camera);
      const loadedTiles = this.getRenderedTiles();

      // Drop rendered tiles that are no longer selected (always keep the root tile).
      const toDelete: IPointCloudTile[] = [];
      for (const tile of loadedTiles) {
        if (tile === this._rootTile) continue;
        if (!wanted.has(tile)) toDelete.push(tile);
      }
      await this.deleteTiles(toDelete);

      // Fetch selected-but-unloaded tiles — coarse/central-first per `ordered`.
      const toFetch = ordered.filter((t) => t.loadingStatus !== "loaded");
      await fetchAndRenderLayer(toFetch);
    }

    return;
  }
  public setHeatMapVisibility(visibility: boolean): void {
    // this._isHeatMapVisible = visibility;
    this._rootTile?.setHeatMapVisibility(visibility);
  }

  /**
   * データが大きい場合、タイルを分割（分割開始位置を遅らせる）計算
   * スライダー初期位置は中央
   * スライダーは0～10、閾値を相対的にずらす（例:-6～+4、-1～9など）
   * @param tlSplitNum - スライダーの値
   * @returns - スライダ値 - (スライダ範囲/2) + (-3 * 幅分割比率);
   */
  public setTileSplit(tlSplitNum: number = 0): void {
    this._tSplitThresHoldNow = tlSplitNum;
  }

  public getTileSplit() {
    return this._tSplitThresHoldNow;
  }

  public toggleIsDebug() {
    this._isDebug = !this._isDebug;
  }
}
